import { useState, useEffect } from 'react';
import { parseAtmXml, parseStopStrategyXml, computeAtmFingerprint } from '../services/ntTemplateParser';

/**
 * Custom hook for ATM strategy management and NT template operations.
 * Extracted from TradingJournal.js (Phase 4).
 *
 * Manages: ATM pattern CRUD, ATM library (rename/delete), ATM details (description/rules),
 *          NinjaTrader template loading & version detection.
 */
const useAtmManagement = ({
  atmPatterns, setAtmPatterns,
  setAtmDetails,
  trades, setTrades,
  ntPaths, isElectron, dataLoaded,
  setNtTemplates,
  setNtTemplatesLoading,
  setNtTemplatesError,
  setNtPathStatus,
  ntTemplateVersions, setNtTemplateVersions,
  setNtVersionChanges,
}) => {

  // ── Internal editing state (owned by this hook) ──
  const [newAtmPatternPrefix, setNewAtmPatternPrefix] = useState('');
  const [newAtmPatternSetup, setNewAtmPatternSetup] = useState('');
  const [editingAtmPatternId, setEditingAtmPatternId] = useState(null);
  const [editAtmPatternPrefix, setEditAtmPatternPrefix] = useState('');
  const [editAtmPatternSetup, setEditAtmPatternSetup] = useState('');
  const [editingAtmName, setEditingAtmName] = useState(null);
  const [editAtmNewName, setEditAtmNewName] = useState('');

  // ── ATM Pattern CRUD ──

  const addAtmPattern = () => {
    if (newAtmPatternPrefix.trim() && newAtmPatternSetup) {
      const newPattern = {
        id: `atm_pattern_${Date.now()}`,
        prefix: newAtmPatternPrefix.trim(),
        setupName: newAtmPatternSetup
      };
      setAtmPatterns([...atmPatterns, newPattern]);
      setNewAtmPatternPrefix('');
      setNewAtmPatternSetup('');
    }
  };

  const removeAtmPattern = (patternId) => {
    setAtmPatterns(atmPatterns.filter(p => p.id !== patternId));
  };

  const startEditingAtmPattern = (pattern) => {
    setEditingAtmPatternId(pattern.id);
    setEditAtmPatternPrefix(pattern.prefix);
    setEditAtmPatternSetup(pattern.setupName);
  };

  const saveEditedAtmPattern = () => {
    if (editAtmPatternPrefix.trim() && editAtmPatternSetup) {
      setAtmPatterns(atmPatterns.map(p =>
        p.id === editingAtmPatternId
          ? { ...p, prefix: editAtmPatternPrefix.trim(), setupName: editAtmPatternSetup }
          : p
      ));
    }
    cancelEditingAtmPattern();
  };

  const cancelEditingAtmPattern = () => {
    setEditingAtmPatternId(null);
    setEditAtmPatternPrefix('');
    setEditAtmPatternSetup('');
  };

  // ── ATM Library Functions ──

  const renameAtmStrategy = (oldName, newName) => {
    if (!newName.trim() || oldName === newName.trim()) return;
    const trimmedNew = newName.trim();

    const updatedTrades = trades.map(trade =>
      trade.atmStrategy === oldName ? { ...trade, atmStrategy: trimmedNew } : trade
    );
    setTrades(updatedTrades);

    const updatedPatterns = atmPatterns.map(pattern =>
      pattern.prefix === oldName ? { ...pattern, prefix: trimmedNew } : pattern
    );
    setAtmPatterns(updatedPatterns);

    setEditingAtmName(null);
    setEditAtmNewName('');
  };

  const deleteAtmStrategy = (atmName) => {
    if (!window.confirm(`Remove ATM "${atmName}" from ${trades.filter(t => t.atmStrategy === atmName).length} trade(s)? This will clear the ATM field for those trades.`)) {
      document.body.focus(); return;
    }
    document.body.focus();

    const updatedTrades = trades.map(trade =>
      trade.atmStrategy === atmName ? { ...trade, atmStrategy: '' } : trade
    );
    setTrades(updatedTrades);
  };

  const startEditingAtm = (atmName) => {
    setEditingAtmName(atmName);
    setEditAtmNewName(atmName);
  };

  const cancelEditingAtm = () => {
    setEditingAtmName(null);
    setEditAtmNewName('');
  };

  const matchAtmToSetup = (atmName) => {
    if (!atmName) return null;
    const atmLower = atmName.toLowerCase();
    for (const pattern of atmPatterns) {
      if (atmLower.startsWith(pattern.prefix.toLowerCase())) {
        return pattern.setupName;
      }
    }
    return null;
  };

  const parseStopLossFromAtm = (atmName) => {
    if (!atmName) return null;
    const slMatch = atmName.match(/(\d+)pSL/i);
    return slMatch ? parseInt(slMatch[1]) : null;
  };

  const getAtmPatternTestResult = (atmName) => {
    if (!atmName) return null;
    const matchedSetup = matchAtmToSetup(atmName);
    const stopLoss = parseStopLossFromAtm(atmName);
    return {
      matchedSetup: matchedSetup || '(no match)',
      stopLoss: stopLoss ? `${stopLoss} points` : '(not detected)'
    };
  };

  const getUniqueAtmStrategies = () => {
    const atmCounts = {};
    trades.forEach(trade => {
      if (trade.atmStrategy) {
        atmCounts[trade.atmStrategy] = (atmCounts[trade.atmStrategy] || 0) + 1;
      }
    });
    return Object.entries(atmCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  };

  // ── ATM Details (description/rules) ──

  const updateAtmDescription = (atmName, description) => {
    setAtmDetails(prev => ({
      ...prev,
      [atmName]: {
        ...(prev[atmName] || {}),
        description
      }
    }));
  };

  const addAtmRule = (atmName, rule) => {
    if (!rule.trim()) return;
    setAtmDetails(prev => ({
      ...prev,
      [atmName]: {
        ...(prev[atmName] || {}),
        rules: [...(prev[atmName]?.rules || []), rule.trim()]
      }
    }));
  };

  const removeAtmRule = (atmName, ruleIndex) => {
    setAtmDetails(prev => ({
      ...prev,
      [atmName]: {
        ...(prev[atmName] || {}),
        rules: (prev[atmName]?.rules || []).filter((_, idx) => idx !== ruleIndex)
      }
    }));
  };

  const updateAtmRule = (atmName, ruleIndex, newText) => {
    setAtmDetails(prev => ({
      ...prev,
      [atmName]: {
        ...(prev[atmName] || {}),
        rules: (prev[atmName]?.rules || []).map((rule, idx) =>
          idx === ruleIndex ? newText : rule
        )
      }
    }));
  };

  // ── NinjaTrader Template Loading ──

  // Archive policy (revised v7.6.0):
  // ── Empty-folder safety: when the NT scan returns ZERO templates (NT
  //    not running, folder unreachable, IPC error), we skip archive
  //    detection entirely. This is the ONLY safety net we need.
  // ── No grace period for individual missing files: if the folder has
  //    other templates but a specific one is gone, that's a deliberate
  //    deletion 99% of the time — archive on the very next sync.
  //
  // Earlier versions used a 1-hour grace window that protected against
  // theoretical "transient single-file disappearance" — a scenario that
  // doesn't really happen. Removing it lets users see their cleanup
  // reflected immediately (matches the v7.6.0 user feedback). Manual
  // Unarchive button is the recovery path if an archive lands wrong.

  const detectTemplateVersionChanges = async (parsedAtmList) => {
    if (!window.electronAPI?.ntTemplates?.loadVersions) return;

    try {
      const loadResult = await window.electronAPI.ntTemplates.loadVersions();
      const existingVersions = loadResult.success ? (loadResult.versions || {}) : {};
      const updatedVersions = { ...existingVersions };
      const changedAtms = [];
      const archivedAtms = [];
      const unarchivedAtms = [];
      const now = new Date().toISOString();

      // Build a Set of names we found in this sync so the missing-detection
      // pass below can do O(1) lookups.
      const atmsInScan = new Set(parsedAtmList.map(a => a.name));

      // ── Pass 1: process templates found in this sync ──
      for (const atm of parsedAtmList) {
        const fingerprint = computeAtmFingerprint(atm);
        if (!fingerprint) continue;

        const atmVersions = updatedVersions[atm.name] || [];
        const currentFingerprint = atmVersions.length > 0 ? atmVersions[0].fingerprint : null;

        if (currentFingerprint !== fingerprint) {
          const newVersion = {
            versionId: `v_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
            detectedAt: now,
            lastSeenAt: now,        // new in v7.6.0 — archive heartbeat
            archivedAt: null,       // new in v7.6.0 — never archived on creation
            label: '',
            fingerprint,
            snapshot: { ...atm, brackets: atm.brackets.map(b => ({ ...b, stopStrategy: b.stopStrategy ? { ...b.stopStrategy, autoTrailSteps: [...(b.stopStrategy.autoTrailSteps || [])] } : null })) }
          };

          updatedVersions[atm.name] = [newVersion, ...atmVersions];

          if (atmVersions.length > 0) {
            changedAtms.push(atm.name);
          }
        } else {
          // Unchanged fingerprint — but we still need to refresh the
          // archive heartbeat fields on the latest version so the next
          // sync knows this template was seen.
          //
          // Manual-archive override (v7.6.0): if the user manually
          // archived this template via the UI (manuallyArchived=true),
          // DON'T auto-clear archivedAt on reappearance — they explicitly
          // marked it inactive. Only the auto-archived-then-reappeared
          // path triggers unarchive. This handles the user's "I have
          // test templates in NT that aren't actively traded" case.
          const latest = atmVersions[0];
          const wasArchived = !!latest?.archivedAt;
          const isManual = !!latest?.manuallyArchived;
          const nextArchivedAt = isManual ? latest.archivedAt : null;
          updatedVersions[atm.name] = [
            { ...latest, lastSeenAt: now, archivedAt: nextArchivedAt },
            ...atmVersions.slice(1),
          ];
          if (wasArchived && !isManual) {
            // Template came back AND wasn't manually archived → toast-worthy event.
            unarchivedAtms.push(atm.name);
          }
        }
      }

      // ── Pass 2: auto-archive missing templates ──
      // Empty-folder safety: if THIS scan returned zero templates (NT not
      // running, folder unreachable, IPC error), do NOT mass-archive
      // everything. Skip the missing-detection entirely.
      if (parsedAtmList.length > 0) {
        for (const atmName of Object.keys(updatedVersions)) {
          if (atmsInScan.has(atmName)) continue;
          const latest = updatedVersions[atmName]?.[0];
          if (!latest) continue;
          if (latest.archivedAt) continue;  // already archived, no-op

          // Template is missing from this sync AND the folder isn't empty
          // (we already short-circuited the empty case above). Archive
          // immediately — no grace period. A single missing file while
          // others are present is essentially always a deliberate
          // deletion; making the user wait an hour to see their cleanup
          // reflected was the wrong default. Manual Unarchive handles
          // the rare false-positive case.
          updatedVersions[atmName] = [
            { ...latest, archivedAt: now },
            ...updatedVersions[atmName].slice(1),
          ];
          archivedAtms.push(atmName);
        }
      }

      // Save when anything changed: new versions, archive transitions, or
      // just lastSeenAt heartbeats (so the grace clock starts on first sync).
      const hasNewVersions = changedAtms.length > 0
        || Object.keys(updatedVersions).length !== Object.keys(existingVersions).length;
      const hasArchiveChanges = archivedAtms.length > 0 || unarchivedAtms.length > 0;
      // Always persist if any template was processed this sync, so lastSeenAt
      // heartbeats are durable. Cheap — file is small.
      if (hasNewVersions || hasArchiveChanges || parsedAtmList.length > 0) {
        await window.electronAPI.ntTemplates.saveVersions(updatedVersions);
      }

      setNtTemplateVersions(updatedVersions);
      setNtVersionChanges(changedAtms);

      if (changedAtms.length > 0) {
        console.log(`[NT Versions] Changes detected: ${changedAtms.join(', ')}`);
      } else {
        console.log(`[NT Versions] No changes (${Object.keys(updatedVersions).length} ATMs tracked)`);
      }
      if (archivedAtms.length > 0) {
        console.log(`[NT Versions] Archived ${archivedAtms.length} template(s) missing from NT: ${archivedAtms.join(', ')}`);
      }
      if (unarchivedAtms.length > 0) {
        console.log(`[NT Versions] Restored ${unarchivedAtms.length} template(s) that reappeared: ${unarchivedAtms.join(', ')}`);
      }
    } catch (e) {
      console.error('[NT Versions] Detection error:', e);
    }
  };

  const loadNtTemplates = async () => {
    if (!isElectron || !window.electronAPI?.ntTemplates) return;
    setNtTemplatesLoading(true);
    setNtTemplatesError(null);
    try {
      const paths = ntPaths.variant === 'v1' ? ntPaths.v1 : ntPaths.v2;
      const result = await window.electronAPI.ntTemplates.readFiles({
        atmPath: paths.atm,
        stopStrategyPath: paths.stop
      });
      if (result.success) {
        const parsedAtm = result.atmFiles.map(f => parseAtmXml(f.xml)).filter(Boolean);
        const parsedStop = result.stopFiles.map(f => parseStopStrategyXml(f.xml)).filter(Boolean);
        setNtTemplates({ atm: parsedAtm, stop: parsedStop });
        console.log(`[NT Templates] Loaded ${parsedAtm.length} ATM, ${parsedStop.length} StopStrategy templates`);

        await detectTemplateVersionChanges(parsedAtm);
      } else {
        setNtTemplatesError(result.error);
      }
    } catch (e) {
      setNtTemplatesError(e.message);
    }
    setNtTemplatesLoading(false);
  };

  const checkNtPaths = async () => {
    if (!isElectron || !window.electronAPI?.ntTemplates) return;
    try {
      const paths = ntPaths.variant === 'v1' ? ntPaths.v1 : ntPaths.v2;
      const result = await window.electronAPI.ntTemplates.checkPaths({
        atmPath: paths.atm,
        stopStrategyPath: paths.stop
      });
      setNtPathStatus(result);
    } catch (e) {
      setNtPathStatus({ success: false, error: e.message });
    }
  };

  const updateVersionLabel = async (atmName, versionId, label) => {
    const updated = { ...ntTemplateVersions };
    const versions = [...(updated[atmName] || [])];
    const idx = versions.findIndex(v => v.versionId === versionId);
    if (idx >= 0) {
      versions[idx] = { ...versions[idx], label };
      updated[atmName] = versions;
      setNtTemplateVersions(updated);
      if (window.electronAPI?.ntTemplates?.saveVersions) {
        await window.electronAPI.ntTemplates.saveVersions(updated);
      }
    }
  };

  // Manual archive (v7.6.0). Lets the user mark a template inactive even
  // when it's still present in NT (test templates, experimental copies,
  // ATMs they're not actively trading but keep in NT for reference) OR
  // when the template was never tracked by the journal in the first place
  // (orphaned: trades reference an ATM name that was deleted from NT
  // before the journal ever ran a sync that saw it).
  //
  // Sets both archivedAt (so all the archive-aware UI treats it as
  // archived) AND manuallyArchived=true (so the sync logic won't auto-
  // clear archivedAt on the next sync that finds the template still in NT).
  //
  // When the template has no prior version record at all, we create a
  // minimal stub so the archive flag has a home — this is the fix for
  // orphaned-from-trades templates that previously couldn't be archived.
  const archiveAtmManually = async (atmName) => {
    if (!atmName) return;
    const updated = { ...ntTemplateVersions };
    const versions = [...(updated[atmName] || [])];
    const now = new Date().toISOString();

    if (versions.length === 0) {
      // Orphaned template — has trades referencing it but the journal has
      // no version snapshot. Create a stub so the archive marker can stick.
      // The snapshot is intentionally sparse: just the name + the archive
      // metadata. Future syncs that find the template will fill in the
      // real fingerprint/brackets via the normal version-change path.
      updated[atmName] = [{
        versionId: `v_stub_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
        detectedAt: now,
        lastSeenAt: null,  // never seen in an NT scan
        archivedAt: now,
        manuallyArchived: true,
        label: 'archived (no NT record)',
        fingerprint: null,
        snapshot: { name: atmName, brackets: [], stub: true },
      }];
    } else {
      versions[0] = {
        ...versions[0],
        archivedAt: versions[0].archivedAt || now,
        manuallyArchived: true,
      };
      updated[atmName] = versions;
    }

    setNtTemplateVersions(updated);
    if (window.electronAPI?.ntTemplates?.saveVersions) {
      await window.electronAPI.ntTemplates.saveVersions(updated);
    }
  };

  // Manual unarchive — clears the archive marker AND the manual flag.
  // The next sync will treat this template normally (auto-archive if
  // missing, stay active if present). Use case: user changed their mind
  // about a manually-archived template, OR an auto-archived template they
  // want to mark active before NT re-creates the file.
  const unarchiveAtmManually = async (atmName) => {
    if (!atmName) return;
    const updated = { ...ntTemplateVersions };
    const versions = [...(updated[atmName] || [])];
    if (versions.length === 0) return;
    versions[0] = {
      ...versions[0],
      archivedAt: null,
      manuallyArchived: false,
    };
    updated[atmName] = versions;
    setNtTemplateVersions(updated);
    if (window.electronAPI?.ntTemplates?.saveVersions) {
      await window.electronAPI.ntTemplates.saveVersions(updated);
    }
  };

  // Auto-load NT templates on mount if in Electron
  useEffect(() => {
    if (dataLoaded && isElectron) {
      loadNtTemplates();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded, isElectron]);

  return {
    // ATM pattern CRUD
    addAtmPattern,
    removeAtmPattern,
    startEditingAtmPattern,
    saveEditedAtmPattern,
    cancelEditingAtmPattern,
    // ATM pattern editing state (for UI)
    newAtmPatternPrefix, setNewAtmPatternPrefix,
    newAtmPatternSetup, setNewAtmPatternSetup,
    editingAtmPatternId,
    editAtmPatternPrefix, setEditAtmPatternPrefix,
    editAtmPatternSetup, setEditAtmPatternSetup,
    // ATM library
    renameAtmStrategy,
    deleteAtmStrategy,
    startEditingAtm,
    cancelEditingAtm,
    matchAtmToSetup,
    parseStopLossFromAtm,
    getAtmPatternTestResult,
    getUniqueAtmStrategies,
    // ATM library editing state (for UI)
    editingAtmName,
    editAtmNewName, setEditAtmNewName,
    // ATM details
    updateAtmDescription,
    addAtmRule,
    removeAtmRule,
    updateAtmRule,
    // NT templates
    loadNtTemplates,
    checkNtPaths,
    detectTemplateVersionChanges,
    updateVersionLabel,
    // Manual archive (v7.6.0) — user-controlled override layered on top
    // of auto-archive. See archiveAtmManually() / unarchiveAtmManually().
    archiveAtmManually,
    unarchiveAtmManually,
  };
};

export default useAtmManagement;
